import { useRef, useState, type DragEvent, type FormEvent } from 'react';
import { api, mensagemDoErro, type RespostaImportacao } from '../api';
import { useApp } from '../estado';
import { useRecurso } from '../ganchos';
import {
  formatarDataCivil,
  formatarDataHora,
  formatarMoeda,
  formatarTelefone,
  mascararDocumento,
} from '../formatacao';
import {
  Aviso,
  Campo,
  Carregando,
  EstadoVazio,
  TituloPagina,
} from '../componentes/ui';

type Aba = 'faturas' | 'titulares';

export function Eventos() {
  const { provedor, notificar } = useApp();
  const eventos = useRecurso(() => api.eventos(), []);
  const optouts = useRecurso(() => api.optouts(), []);

  const [aba, setAba] = useState<Aba>('faturas');
  const [arrastando, setArrastando] = useState(false);
  const [arquivo, setArquivo] = useState<{ nome: string; tamanho: number; conteudo: string } | null>(
    null,
  );
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState<RespostaImportacao | null>(null);
  const entradaArquivo = useRef<HTMLInputElement>(null);

  const [documento, setDocumento] = useState('');
  const [motivo, setMotivo] = useState('pedido do Titular');
  const [erroOptout, setErroOptout] = useState<string | null>(null);
  const [salvandoOptout, setSalvandoOptout] = useState(false);

  const fuso = provedor?.fuso ?? 'America/Sao_Paulo';
  const dados = eventos.dados;
  const listaOptouts = optouts.dados?.optouts ?? [];

  const lerArquivo = (file: File) => {
    if (file.size > 20 * 1024 * 1024) {
      notificar('erro', 'O arquivo passa de 20 MB. Divida a exportação em partes.');
      return;
    }
    const leitor = new FileReader();
    leitor.onload = () => {
      const texto = typeof leitor.result === 'string' ? leitor.result : '';
      setArquivo({ nome: file.name, tamanho: file.size, conteudo: texto });
      setResultado(null);
    };
    leitor.onerror = () => notificar('erro', 'Não foi possível ler o arquivo.');
    leitor.readAsText(file);
  };

  const soltar = (evento: DragEvent<HTMLDivElement>) => {
    evento.preventDefault();
    setArrastando(false);
    const file = evento.dataTransfer.files?.[0];
    if (file) lerArquivo(file);
  };

  const importar = async () => {
    if (!arquivo) return;
    setImportando(true);
    try {
      const formato =
        arquivo.nome.toLowerCase().endsWith('.json')
          ? 'json'
          : arquivo.nome.toLowerCase().endsWith('.csv')
            ? 'csv'
            : 'auto';
      const resposta = await api.importar(arquivo.nome, arquivo.conteudo, formato);
      setResultado(resposta);
      notificar(
        'sucesso',
        `${resposta.faturas} fatura(s) e ${resposta.titulares} Titular(es) importados.`,
      );
      setArquivo(null);
      if (entradaArquivo.current) entradaArquivo.current.value = '';
      await eventos.recarregar();
    } catch (falha) {
      notificar('erro', mensagemDoErro(falha));
    } finally {
      setImportando(false);
    }
  };

  const baixarModelo = () => {
    if (!dados) return;
    const blob = new Blob([dados.modelo], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'modelo-importacao.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const adicionarOptout = async (evento: FormEvent<HTMLFormElement>) => {
    evento.preventDefault();
    setErroOptout(null);
    const digitos = documento.replace(/\D/g, '');
    if (digitos.length !== 11 && digitos.length !== 14) {
      setErroOptout('Informe um CPF (11 dígitos) ou CNPJ (14 dígitos).');
      return;
    }
    setSalvandoOptout(true);
    try {
      await api.adicionarOptout(digitos, motivo.trim() || 'pedido do Titular');
      notificar('sucesso', 'Titular incluído na lista de não perturbe.');
      setDocumento('');
      setMotivo('pedido do Titular');
      await optouts.recarregar();
    } catch (falha) {
      setErroOptout(mensagemDoErro(falha));
    } finally {
      setSalvandoOptout(false);
    }
  };

  const removerOptout = async (doc: string) => {
    try {
      await api.removerOptout(doc);
      notificar('info', 'Opt-out removido. O Titular volta a receber as réguas.');
      await optouts.recarregar();
    } catch (falha) {
      notificar('erro', mensagemDoErro(falha));
    }
  };

  return (
    <>
      <TituloPagina
        titulo="Eventos e Titulares"
        descricao="Gerencie a base de demonstração e os arquivos importados. No modo real, o planejamento consulta no ELO os eventos necessários para as Receitas ativas."
        acoes={
          <>
            <button type="button" className="botao botao--secundario" onClick={baixarModelo}>
              Baixar modelo CSV
            </button>
            <button
              type="button"
              className="botao botao--primario"
              onClick={() => entradaArquivo.current?.click()}
            >
              Escolher arquivo
            </button>
          </>
        }
      />

      <section className="cartao" aria-labelledby="importar-titulo">
        <div className="cartao__cabecalho">
          <div className="cartao__titulo">
            <h2 id="importar-titulo">Importar do ERP</h2>
            <p className="cartao__descricao">
              CSV ou JSON. Linhas inválidas não derrubam o arquivo: entram no relatório para
              correção. Reimportar o mesmo dia atualiza os eventos, sem duplicar. A importação não
              substitui nem complementa uma consulta ELO em modo real.
            </p>
          </div>
          {dados?.ultimaImportacao ? (
            <span className="texto-pequeno">
              Última: {dados.ultimaImportacao.arquivo} ·{' '}
              {formatarDataHora(dados.ultimaImportacao.criadoEm, fuso)}
            </span>
          ) : null}
        </div>

        <div
          className="dropzone"
          data-ativo={arrastando}
          onDragOver={(evento) => {
            evento.preventDefault();
            setArrastando(true);
          }}
          onDragLeave={() => setArrastando(false)}
          onDrop={soltar}
        >
          <span aria-hidden="true" style={{ fontSize: '1.6rem' }}>
            ⤓
          </span>
          <p>
            <strong>Arraste o arquivo aqui</strong> ou use “Escolher arquivo”.
          </p>
          <p className="dropzone__texto">
            Colunas esperadas: tipo, documento, nome, telefone, contrato, fatura, vencimento,
            valor, situacao, nascimento. Até 20 MB.
          </p>
          <input
            ref={entradaArquivo}
            type="file"
            accept=".csv,.json,.txt,text/csv,application/json"
            className="sr-apenas"
            aria-label="Arquivo de eventos"
            onChange={(evento) => {
              const file = evento.target.files?.[0];
              if (file) lerArquivo(file);
            }}
          />
        </div>

        {arquivo ? (
          <div className="linha linha--separada" style={{ marginTop: '1rem' }}>
            <span className="texto-pequeno">
              <strong>{arquivo.nome}</strong> · {(arquivo.tamanho / 1024).toFixed(1)} KB prontos
              para importar
            </span>
            <div className="linha">
              <button
                type="button"
                className="botao botao--fantasma botao--pequeno"
                onClick={() => {
                  setArquivo(null);
                  if (entradaArquivo.current) entradaArquivo.current.value = '';
                }}
              >
                Descartar
              </button>
              <button
                type="button"
                className="botao botao--primario"
                onClick={() => void importar()}
                disabled={importando}
              >
                {importando ? 'Importando…' : 'Importar arquivo'}
              </button>
            </div>
          </div>
        ) : null}

        {resultado ? (
          <div style={{ marginTop: '1rem' }}>
            <Aviso
              tipo={resultado.ignorados > 0 ? 'alerta' : 'ok'}
              titulo={`${resultado.faturas} fatura(s), ${resultado.titulares} Titular(es), ${resultado.ignorados} linha(s) ignorada(s)`}
            >
              {resultado.erros.length === 0 ? (
                'Arquivo lido sem erros.'
              ) : (
                <ul style={{ margin: 0 }}>
                  {resultado.erros.map((erro) => (
                    <li key={erro}>{erro}</li>
                  ))}
                </ul>
              )}
            </Aviso>
          </div>
        ) : null}
      </section>

      <section className="grade grade--kpi" aria-label="Eventos importados">
        <article className="kpi">
          <span className="kpi__rotulo">Faturas</span>
          <span className="kpi__valor">{dados?.contagens.faturas ?? '—'}</span>
          <span className="kpi__detalhe">Eventos de cobrança disponíveis</span>
        </article>
        <article className="kpi kpi--info">
          <span className="kpi__rotulo">Titulares</span>
          <span className="kpi__valor">{dados?.contagens.titulares ?? '—'}</span>
          <span className="kpi__detalhe">Base para aniversário e opt-out</span>
        </article>
        <article className="kpi kpi--erro">
          <span className="kpi__rotulo">Opt-out</span>
          <span className="kpi__valor">{listaOptouts.length}</span>
          <span className="kpi__detalhe">Documentos que nunca recebem</span>
        </article>
      </section>

      <section className="cartao" aria-labelledby="base-titulo">
        <div className="cartao__cabecalho">
          <div className="cartao__titulo">
            <h2 id="base-titulo">Base importada</h2>
            <p className="cartao__descricao">Até 200 registros por consulta, ordenados pelo mais recente.</p>
          </div>
          <div className="segmentado" role="group" aria-label="Escolher tabela">
            <button
              type="button"
              aria-pressed={aba === 'faturas'}
              onClick={() => setAba('faturas')}
            >
              Faturas ({dados?.contagens.faturas ?? 0})
            </button>
            <button
              type="button"
              aria-pressed={aba === 'titulares'}
              onClick={() => setAba('titulares')}
            >
              Titulares ({dados?.contagens.titulares ?? 0})
            </button>
          </div>
        </div>

        {eventos.carregando && !dados ? (
          <Carregando texto="Lendo a base importada…" />
        ) : eventos.erro && !dados ? (
          <Aviso tipo="erro" titulo="Não foi possível ler os eventos">
            {eventos.erro}
          </Aviso>
        ) : aba === 'faturas' ? (
          (dados?.faturas.length ?? 0) === 0 ? (
            <EstadoVazio
              titulo="Nenhuma fatura importada"
              texto="Importe o arquivo do ERP para alimentar as réguas de cobrança."
              icone="⤓"
            />
          ) : (
            <div className="tabela-rolagem">
              <table>
                <caption className="sr-apenas">Faturas importadas</caption>
                <thead>
                  <tr>
                    <th scope="col">Titular</th>
                    <th scope="col">Contrato</th>
                    <th scope="col">Fatura</th>
                    <th scope="col">Vencimento</th>
                    <th scope="col">Valor</th>
                    <th scope="col">Situação</th>
                    <th scope="col">Telefone</th>
                  </tr>
                </thead>
                <tbody>
                  {dados?.faturas.map((fatura) => (
                    <tr key={fatura.id}>
                      <td>
                        <strong>{fatura.nome}</strong>
                        <br />
                        <span className="texto-pequeno">{mascararDocumento(fatura.documento)}</span>
                      </td>
                      <td className="mono">{fatura.contrato || '—'}</td>
                      <td className="mono">{fatura.fatura}</td>
                      <td className="numero">{formatarDataCivil(fatura.vencimento)}</td>
                      <td className="numero">{formatarMoeda(fatura.valorCentavos)}</td>
                      <td>
                        <span
                          className={`badge ${
                            fatura.situacao === 'paga' ? 'badge--ok' : 'badge--alerta'
                          }`}
                        >
                          <span aria-hidden="true">
                            {fatura.situacao === 'paga' ? '✓' : '◷'}
                          </span>
                          {fatura.situacao === 'paga' ? 'Paga' : 'Em aberto'}
                        </span>
                      </td>
                      <td>{formatarTelefone(fatura.telefone)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (dados?.titulares.length ?? 0) === 0 ? (
          <EstadoVazio
            titulo="Nenhum Titular importado"
            texto="Inclua a data de nascimento no arquivo para ativar a receita de aniversário."
            icone="⤓"
          />
        ) : (
          <div className="tabela-rolagem">
            <table>
              <caption className="sr-apenas">Titulares importados</caption>
              <thead>
                <tr>
                  <th scope="col">Nome</th>
                  <th scope="col">Documento</th>
                  <th scope="col">Contrato</th>
                  <th scope="col">Nascimento</th>
                  <th scope="col">Telefone</th>
                </tr>
              </thead>
              <tbody>
                {dados?.titulares.map((titular) => (
                  <tr key={titular.id}>
                    <td>
                      <strong>{titular.nome}</strong>
                    </td>
                    <td>{mascararDocumento(titular.documento)}</td>
                    <td className="mono">{titular.contrato || '—'}</td>
                    <td className="numero">{formatarDataCivil(titular.nascimento)}</td>
                    <td>{formatarTelefone(titular.telefone)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="cartao" aria-labelledby="optout-titulo">
        <div className="cartao__cabecalho">
          <div className="cartao__titulo">
            <h2 id="optout-titulo">Lista de não perturbe</h2>
            <p className="cartao__descricao">
              Opt-out é por Provedor e vale para todas as receitas. É verificado antes de cada
              envio, inclusive nos reprocessamentos.
            </p>
          </div>
        </div>

        <form className="filtros filtros--campos" onSubmit={adicionarOptout}>
          <Campo rotulo="CPF ou CNPJ" erro={erroOptout ?? undefined}>
            <input
              className="entrada"
              value={documento}
              onChange={(evento) => setDocumento(evento.target.value)}
              placeholder="123.456.789-00"
              inputMode="numeric"
            />
          </Campo>
          <Campo rotulo="Motivo" ajuda="Fica registrado na auditoria.">
            <input
              className="entrada"
              value={motivo}
              onChange={(evento) => setMotivo(evento.target.value)}
              placeholder="pedido do Titular"
            />
          </Campo>
          {/* Rótulo vazio: alinha o botão à faixa dos inputs, não ao rodapé
              da ajuda do campo vizinho. */}
          <div className="campo campo--acao">
            <span className="campo__rotulo" aria-hidden="true">
              &nbsp;
            </span>
            <button type="submit" className="botao botao--secundario" disabled={salvandoOptout}>
              {salvandoOptout ? 'Salvando…' : 'Adicionar opt-out'}
            </button>
          </div>
        </form>

        <div style={{ marginTop: '1rem' }}>
          {listaOptouts.length === 0 ? (
            <p className="texto-pequeno">Nenhum Titular pediu para não receber.</p>
          ) : (
            <div className="tabela-rolagem">
              <table>
                <caption className="sr-apenas">Titulares em opt-out</caption>
                <thead>
                  <tr>
                    <th scope="col">Documento</th>
                    <th scope="col">Motivo</th>
                    <th scope="col">Registrado em</th>
                    <th scope="col">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {listaOptouts.map((optout) => (
                    <tr key={optout.documento}>
                      <td className="mono">{mascararDocumento(optout.documento)}</td>
                      <td>{optout.motivo}</td>
                      <td className="numero">{formatarDataHora(optout.criadoEm, fuso)}</td>
                      <td>
                        <button
                          type="button"
                          className="botao botao--perigo botao--pequeno"
                          onClick={() => void removerOptout(optout.documento)}
                        >
                          Remover
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
